/* eslint-disable unicode-bom */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSearch, FiX, FiEye, FiPlus, FiTrash2, FiDownload, FiPrinter, FiRepeat } from 'react-icons/fi';
import './NilaiTransfer.css';

const NilaiTransfer = () => {
  const [transferList, setTransferList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [newTransferForm, setNewTransferForm] = useState({
    nim: '',
    nama_mahasiswa: '',
    kode_mk_asal: '',
    nama_mk_asal: '',
    sks_asal: '',
    nilai_asal: '',
    kode_mk_diakui: '',
    nama_mk_diakui: '',
    sks_diakui: '',
    nilai_huruf: '',
    nilai_angka: '',
    semester_transfer: '',
    tahun_transfer: '',
    perguruan_tinggi_asal: '',
    keterangan: ''
  });
  const [syncConfig, setSyncConfig] = useState({
    limit: 50,
    syncMode: 'upsert',
    tahunMasuk: '',
    nim: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    totalSks: 0,
    byStudent: []
  });

  useEffect(() => {
    fetchTransfer();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchTransfer = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;

      const res = await axios.get('/api/nilai-transfer', { params });
      setTransferList(res.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal mengambil data nilai transfer');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/nilai-transfer/stats');
      setStats(res.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSyncTransfer = async () => {
    setSyncing(true);
    try {
      const res = await axios.post('/api/neofeeder/sync/nilai-transfer', syncConfig);
      toast.success(res.data.message || 'Sinkronisasi nilai transfer berhasil!');
      setShowSyncModal(false);
      fetchTransfer();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Sinkronisasi nilai transfer gagal');
      console.error('Sync error:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleViewDetail = async (transfer) => {
    try {
      // Fetch all transfer data for this student
      const res = await axios.get(`/api/nilai-transfer/student/${transfer.nim}`);
      const responseData = res.data.data;
      const studentTransfers = responseData.transfers || [];
      
      // Calculate totals and get unique semesters
      const totalMK = studentTransfers.length;
      const totalSKS = studentTransfers.reduce((sum, t) => sum + (t.sks_diakui || 0), 0);
      const semesters = [...new Set(studentTransfers.map(t => t.semester_transfer).filter(Boolean))].sort();
      
      setSelectedTransfer({
        ...transfer,
        totalMK,
        totalSKS,
        semesters
      });
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching student transfers:', error);
      toast.error('Gagal memuat detail nilai transfer');
    }
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTransfer(null);
  };

  const handleNewTransferInputChange = (field, value) => {
    setNewTransferForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTransfer = async () => {
    try {
      await axios.post('/api/nilai-transfer', newTransferForm);
      toast.success('Nilai transfer berhasil ditambahkan');
      setShowAddModal(false);
      setNewTransferForm({
        nim: '',
        nama_mahasiswa: '',
        kode_mk_asal: '',
        nama_mk_asal: '',
        sks_asal: '',
        nilai_asal: '',
        kode_mk_diakui: '',
        nama_mk_diakui: '',
        sks_diakui: '',
        nilai_huruf: '',
        nilai_angka: '',
        semester_transfer: '',
        tahun_transfer: '',
        perguruan_tinggi_asal: '',
        keterangan: ''
      });
      fetchTransfer();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal menambahkan nilai transfer');
    }
  };

  const handleDeleteTransfer = async (transferId) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus nilai transfer ini?')) {
      try {
        await axios.delete(`/api/nilai-transfer/${transferId}`);
        toast.success('Nilai transfer berhasil dihapus');
        fetchTransfer();
        fetchStats();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Gagal menghapus nilai transfer');
      }
    }
  };

  const handlePrintTransfer = (nim) => {
    if (!nim) {
      toast.warning('NIM mahasiswa tidak valid');
      return;
    }
    
    const token = localStorage.getItem('token');
    const printUrl = `http://localhost:5000/api/nilai-transfer/print/${nim}?token=${token}`;
    
    // Open print page in new window
    const printWindow = window.open(printUrl, '_blank');
    
    if (!printWindow) {
      toast.error('Pop-up diblokir. Harap izinkan pop-up untuk mencetak.');
    }
  };

  if (loading && transferList.length === 0) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="nilai-transfer-page">
      {/* Modern Transparent Header */}
      <div className="page-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <FiRepeat className="header-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">Nilai Transfer</h1>
              <p className="header-subtitle">Kelola data nilai transfer mahasiswa dari NEO Feeder</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-glass btn-primary" onClick={() => setShowAddModal(true)}>
              <FiPlus /> 
              <span className="btn-text">Tambah Nilai Transfer</span>
            </button>
            <button className="btn btn-glass btn-success" onClick={() => setShowSyncModal(true)}>
              <FiDownload /> 
              <span className="btn-text">Sinkronisasi</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#4f46e5' }}>
            <FiRepeat />
          </div>
          <div className="stat-details">
            <h3>{stats.total || 0}</h3>
            <p>Total Nilai Transfer</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#10b981' }}>
            <FiDownload />
          </div>
          <div className="stat-details">
            <h3>{stats.totalSks || 0}</h3>
            <p>Total SKS Diakui</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#f59e0b' }}>
            <FiEye />
          </div>
          <div className="stat-details">
            <h3>{stats.byStudent?.length || 0}</h3>
            <p>Mahasiswa dengan Transfer</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="filters">
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Cari NIM, nama mahasiswa, atau mata kuliah..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Transfer Table */}
      <div className="card">
        <h3>Daftar Nilai Transfer</h3>
        {transferList.length === 0 ? (
          <div className="empty-state">
            <p>
              {searchTerm
                ? 'Tidak ada nilai transfer yang sesuai dengan pencarian'
                : 'Belum ada data nilai transfer. Silakan tambah nilai transfer atau lakukan sinkronisasi dari NEO Feeder.'}
            </p>
          </div>
        ) : (
          <div className="modern-table-container">
            <table className="modern-table accent-purple">
              <thead>
                <tr>
                  <th>NIM</th>
                  <th>Nama Mahasiswa</th>
                  <th>MK Asal</th>
                  <th>MK Diakui</th>
                  <th>SKS</th>
                  <th>Nilai</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {transferList.map((transfer) => (
                  <tr key={transfer._id}>
                    <td>{transfer.nim || '-'}</td>
                    <td><strong>{transfer.nama_mahasiswa || '-'}</strong></td>
                    <td>
                      {transfer.nama_mk_asal || '-'}
                      <br />
                      <small style={{ color: '#6b7280' }}>({transfer.kode_mk_asal || '-'})</small>
                    </td>
                    <td>
                      {transfer.nama_mk_diakui || '-'}
                      <br />
                      <small style={{ color: '#6b7280' }}>({transfer.kode_mk_diakui || '-'})</small>
                    </td>
                    <td>{transfer.sks_diakui || transfer.sks_asal || '-'}</td>
                    <td>
                      <span className={`grade-badge grade-${transfer.nilai_huruf}`}>
                        {transfer.nilai_huruf || '-'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="table-btn table-btn-view"
                          onClick={() => handleViewDetail(transfer)}
                          title="Lihat Detail"
                        >
                          <FiEye />
                        </button>
                        <button
                          className="table-btn table-btn-primary"
                          onClick={() => handlePrintTransfer(transfer.nim)}
                          title="Cetak Nilai Transfer"
                        >
                          <FiPrinter />
                        </button>
                        <button
                          className="table-btn table-btn-delete"
                          onClick={() => handleDeleteTransfer(transfer._id)}
                          title="Hapus"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Detail */}
      {showModal && selectedTransfer && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detail Nilai Transfer Mahasiswa</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              {/* Data Mahasiswa */}
              <div className="detail-section">
                <h4 className="section-title">Data Mahasiswa</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>NIM</label>
                    <p><strong>{selectedTransfer.nim || '-'}</strong></p>
                  </div>
                  <div className="detail-item">
                    <label>Nama Mahasiswa</label>
                    <p><strong>{selectedTransfer.nama_mahasiswa || '-'}</strong></p>
                  </div>
                  <div className="detail-item">
                    <label>Perguruan Tinggi Asal</label>
                    <p>{selectedTransfer.perguruan_tinggi_asal || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Summary Transfer */}
              <div className="detail-section">
                <h4 className="section-title">Ringkasan Nilai Transfer</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Total Mata Kuliah Transfer</label>
                    <p><strong className="text-primary">{selectedTransfer.totalMK || 0}</strong> Mata Kuliah</p>
                  </div>
                  <div className="detail-item">
                    <label>Total SKS Diakui</label>
                    <p><strong className="text-success">{selectedTransfer.totalSKS || 0}</strong> SKS</p>
                  </div>
                  <div className="detail-item">
                    <label>Semester Transfer</label>
                    <p>{selectedTransfer.semesters && selectedTransfer.semesters.length > 0 
                        ? selectedTransfer.semesters.join(', ') 
                        : '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Add Transfer */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Tambah Nilai Transfer Baru</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h4 className="section-title">Data Mahasiswa</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>NIM *</label>
                    <input
                      type="text"
                      className="edit-input"
                      value={newTransferForm.nim}
                      onChange={(e) => handleNewTransferInputChange('nim', e.target.value)}
                      required
                    />
                  </div>
                  <div className="detail-item">
                    <label>Nama Mahasiswa *</label>
                    <input
                      type="text"
                      className="edit-input"
                      value={newTransferForm.nama_mahasiswa}
                      onChange={(e) => handleNewTransferInputChange('nama_mahasiswa', e.target.value)}
                      required
                    />
                  </div>
                  <div className="detail-item">
                    <label>Perguruan Tinggi Asal</label>
                    <input
                      type="text"
                      className="edit-input"
                      value={newTransferForm.perguruan_tinggi_asal}
                      onChange={(e) => handleNewTransferInputChange('perguruan_tinggi_asal', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4 className="section-title">Mata Kuliah Asal</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Kode MK</label>
                    <input
                      type="text"
                      className="edit-input"
                      value={newTransferForm.kode_mk_asal}
                      onChange={(e) => handleNewTransferInputChange('kode_mk_asal', e.target.value)}
                    />
                  </div>
                  <div className="detail-item">
                    <label>Nama MK</label>
                    <input
                      type="text"
                      className="edit-input"
                      value={newTransferForm.nama_mk_asal}
                      onChange={(e) => handleNewTransferInputChange('nama_mk_asal', e.target.value)}
                    />
                  </div>
                  <div className="detail-item">
                    <label>SKS</label>
                    <input
                      type="number"
                      className="edit-input"
                      value={newTransferForm.sks_asal}
                      onChange={(e) => handleNewTransferInputChange('sks_asal', e.target.value)}
                    />
                  </div>
                  <div className="detail-item">
                    <label>Nilai</label>
                    <input
                      type="text"
                      className="edit-input"
                      value={newTransferForm.nilai_asal}
                      onChange={(e) => handleNewTransferInputChange('nilai_asal', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4 className="section-title">Mata Kuliah Diakui</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Kode MK</label>
                    <input
                      type="text"
                      className="edit-input"
                      value={newTransferForm.kode_mk_diakui}
                      onChange={(e) => handleNewTransferInputChange('kode_mk_diakui', e.target.value)}
                    />
                  </div>
                  <div className="detail-item">
                    <label>Nama MK</label>
                    <input
                      type="text"
                      className="edit-input"
                      value={newTransferForm.nama_mk_diakui}
                      onChange={(e) => handleNewTransferInputChange('nama_mk_diakui', e.target.value)}
                    />
                  </div>
                  <div className="detail-item">
                    <label>SKS Diakui</label>
                    <input
                      type="number"
                      className="edit-input"
                      value={newTransferForm.sks_diakui}
                      onChange={(e) => handleNewTransferInputChange('sks_diakui', e.target.value)}
                    />
                  </div>
                  <div className="detail-item">
                    <label>Nilai Huruf</label>
                    <select
                      className="edit-select"
                      value={newTransferForm.nilai_huruf}
                      onChange={(e) => handleNewTransferInputChange('nilai_huruf', e.target.value)}
                    >
                      <option value="">Pilih</option>
                      <option value="A">A</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B">B</option>
                      <option value="B-">B-</option>
                      <option value="C+">C+</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                      <option value="E">E</option>
                    </select>
                  </div>
                  <div className="detail-item">
                    <label>Nilai Angka</label>
                    <input
                      type="number"
                      step="0.01"
                      className="edit-input"
                      value={newTransferForm.nilai_angka}
                      onChange={(e) => handleNewTransferInputChange('nilai_angka', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4 className="section-title">Informasi Transfer</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Semester Transfer</label>
                    <input
                      type="text"
                      className="edit-input"
                      value={newTransferForm.semester_transfer}
                      onChange={(e) => handleNewTransferInputChange('semester_transfer', e.target.value)}
                    />
                  </div>
                  <div className="detail-item">
                    <label>Tahun Transfer</label>
                    <input
                      type="text"
                      className="edit-input"
                      value={newTransferForm.tahun_transfer}
                      onChange={(e) => handleNewTransferInputChange('tahun_transfer', e.target.value)}
                    />
                  </div>
                  <div className="detail-item full-width">
                    <label>Keterangan</label>
                    <textarea
                      className="edit-input"
                      value={newTransferForm.keterangan}
                      onChange={(e) => handleNewTransferInputChange('keterangan', e.target.value)}
                      rows="3"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Batal
              </button>
              <button
                className="btn btn-success"
                onClick={handleAddTransfer}
              >
                <FiPlus /> Tambah Nilai Transfer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Sync Config */}
      {showSyncModal && (
        <div className="modal-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="modal-content modal-sync" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Sinkronisasi Data Nilai Transfer</h3>
              <button className="modal-close" onClick={() => setShowSyncModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="sync-info">
                <p>Mengambil data nilai transfer mahasiswa dari Neo Feeder</p>
              </div>

              <div className="form-group">
                <label>Mode Sinkronisasi</label>
                <select
                  className="form-control"
                  value={syncConfig.syncMode}
                  onChange={(e) => setSyncConfig({ ...syncConfig, syncMode: e.target.value })}
                >
                  <option value="upsert">Upsert (Insert + Update)</option>
                  <option value="insert">Insert Only (Tambah Baru)</option>
                  <option value="update">Update Only (Update Existing)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Jumlah Data (Limit)</label>
                <input
                  type="number"
                  className="form-control"
                  value={syncConfig.limit}
                  onChange={(e) => setSyncConfig({ ...syncConfig, limit: parseInt(e.target.value) || 50 })}
                  min="1"
                  max="1000"
                />
              </div>

              <div className="form-group">
                <label>Filter Tahun Masuk (Opsional)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Contoh: 2023"
                  value={syncConfig.tahunMasuk}
                  onChange={(e) => setSyncConfig({ ...syncConfig, tahunMasuk: e.target.value })}
                  maxLength="4"
                />
                <small className="form-text">
                  Kosongkan untuk mengambil semua data. Format: 4 digit tahun (contoh: 2023)
                </small>
              </div>

              <div className="form-group">
                <label>Filter NIM Mahasiswa (Opsional)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Contoh: 2023123456"
                  value={syncConfig.nim}
                  onChange={(e) => setSyncConfig({ ...syncConfig, nim: e.target.value })}
                />
                <small className="form-text">
                  Kosongkan untuk mengambil semua data. Isi NIM lengkap untuk sinkronisasi data mahasiswa tertentu
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowSyncModal(false)}
              >
                Batal
              </button>
              <button
                className="btn btn-success"
                onClick={handleSyncTransfer}
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <div className="spinner-small"></div>
                    Sinkronisasi...
                  </>
                ) : (
                  <>
                    <FiDownload /> Mulai Sinkronisasi
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NilaiTransfer;
